import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
} from "reactstrap";
import FontAwesomeIcon from "../../helpers/fontawesome/fontAwesome";
import NumberFormat from "react-number-format";
import moment from "moment";
import { addBalance } from "../../helpers/api/banks";
import { fetchLiveExchangeRate } from "../../helpers/api/currency";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import { Toast } from "../../components/UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { CurrencyContext } from "../../context/Context";
import { formatAmountForBackend, getCurrencySymbol } from "../../helpers/currencyConversion";
import { useSelector } from "react-redux";
import FileUploadArea from "./FileUploadArea";

const isBolivaresCurrency = (bankCurrency) => {
  const name = (bankCurrency?.name || "").toLowerCase();
  const iso = (bankCurrency?.iso || "").toLowerCase();
  const symbol = (bankCurrency?.symbol || "").toLowerCase();

  return (
    name.includes("boliva") ||
    iso === "ves" ||
    iso === "bs" ||
    symbol === "bs"
  );
};

const resolveCurrencySymbol = (bankCurrency, fallbackCurrency) => {
  if (bankCurrency?.symbol) return bankCurrency.symbol;
  if (isBolivaresCurrency(bankCurrency)) return "Bs";
  if (bankCurrency?.iso) return getCurrencySymbol(bankCurrency.iso);
  return fallbackCurrency?.symbol || "$";
};

const BankListItem = ({
  mutate,
  title,
  alternativeName,
  onDeleteAction,
  onEditAction,
  options,
  createdAt,
  owner_name,
  currency,
  email,
  owner_dni,
  account_number,
  toEditId,
  toDeleteId,
  ...rest
}) => {
  const { baseCurrency } = useContext(CurrencyContext);
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [nameValidation, setNameValidation] = useState(null);
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceData, setBalanceData] = useState({ amount: "", description: "" });
  const [voucherFile, setVoucherFile] = useState(null);
  const [voucherPreview, setVoucherPreview] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [isRateLoading, setIsRateLoading] = useState(false);

  const currencyLabel = useMemo(() => {
    const currencyIso = currency?.iso || baseCurrency?.iso || "USD";
    const currencyName = currency?.name || baseCurrency?.name || "Moneda base";

    return `(${currencyIso}) ${currencyName}`;
  }, [baseCurrency, currency]);

  const currencySymbol = useMemo(
    () => resolveCurrencySymbol(currency, baseCurrency),
    [baseCurrency, currency]
  );

  const isBaseCurrencyBank = useMemo(() => {
    const bankIso = (currency?.iso || baseCurrency?.iso || "USD").toUpperCase();
    return bankIso === (baseCurrency?.iso || "USD").toUpperCase();
  }, [baseCurrency?.iso, currency?.iso]);

  const convertedBalanceAmount = useMemo(() => {
    const rawAmount = Number(balanceData.amount || 0);

    if (!rawAmount) {
      return 0;
    }

    if (isBaseCurrencyBank) {
      return rawAmount;
    }

    const numericRate = Number(exchangeRate || 0);
    if (!(numericRate > 0)) {
      return 0;
    }

    return rawAmount / numericRate;
  }, [balanceData.amount, exchangeRate, isBaseCurrencyBank]);

  const rateHelpText = useMemo(() => {
    if (isBaseCurrencyBank) {
      return "";
    }

    if (isRateLoading) {
      return "Consultando tasa...";
    }

    if (!(Number(exchangeRate || 0) > 0)) {
      return "";
    }

    return `Tasa ${moment().format("DD/MM/YYYY")}: ${Number(exchangeRate).toFixed(2)}`;
  }, [exchangeRate, isBaseCurrencyBank, isRateLoading]);

  useEffect(() => {
    let isMounted = true;

    const loadExchangeRate = async () => {
      if (!balanceModal || isBaseCurrencyBank || !selectedCondominium) {
        if (isMounted) {
          setExchangeRate(null);
          setIsRateLoading(false);
        }
        return;
      }

      if (isMounted) {
        setIsRateLoading(true);
      }

      try {
        const response = await fetchLiveExchangeRate({
          condominium_id: selectedCondominium,
          date: moment().format("YYYY-MM-DD"),
        });

        if (isMounted) {
          setExchangeRate(Number(response?.value || 0));
        }
      } catch (error) {
        if (isMounted) {
          setExchangeRate(null);
          handleErrorMessage(error);
        }
      } finally {
        if (isMounted) {
          setIsRateLoading(false);
        }
      }
    };

    loadExchangeRate();

    return () => {
      isMounted = false;
    };
  }, [balanceModal, isBaseCurrencyBank, selectedCondominium]);

  const handleDropdownAction = (action) => {
    if (action === "edit") {
      onEditAction({ toEditId });
      return;
    }
    if (action === "delete") return collapseOneOpen(true);
    if (action === "add-balance") return setBalanceModal(true);
    throw new Error("invalid action");
  };

  const handleDelete = () => {
    collapseOneOpen(false);
    onDeleteAction({ toDeleteId });
  };

  const handleAddBalance = async () => {
    try {
      if (!balanceData.amount || balanceData.amount <= 0) {
        Toast.fire({
          type: "error",
          title: "Por favor ingresa un monto válido.",
        });
        return;
      }

      if (!isBaseCurrencyBank && !(Number(exchangeRate || 0) > 0)) {
        Toast.fire({
          type: "error",
          title: "No hay una tasa disponible para convertir este saldo a la moneda base.",
        });
        return;
      }

      const payload = {
        ...balanceData,
        amount: formatAmountForBackend(convertedBalanceAmount || 0),
        ...(voucherFile ? { voucher: voucherFile } : {}),
      };

      if (!isBaseCurrencyBank) {
        payload.amount_bs = formatAmountForBackend(balanceData.amount || 0);
        payload.rate = Number(exchangeRate);
      }

      const response = await addBalance(toEditId, payload);
      if (response.code === SUCCESS_RESPONSE.OK) {
        Toast.fire({
          type: "success",
          title: "Saldo agregado satisfactoriamente.",
        });
        setBalanceModal(false);
        setBalanceData({ amount: "", description: "" });
        setVoucherFile(null);
        setVoucherPreview(null);
        if (mutate) mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const validateCondominiumNameOnDelete = () => {
    return nameValidation === title;
  };

  const handleModal = () => (
    <Modal isOpen={collapseOne} data-testid="modal">
      <ModalHeader toggle={() => collapseOneOpen(!collapseOne)}>Eliminar {title}</ModalHeader>
      <ModalBody>
        <p className="mb-0 font-weight-bold fs--1">
          Para continuar ingresa el nombre del {alternativeName} {title}
        </p>
        <FormGroup className="mt-1">
          <Input
            data-testid="validation-input"
            type="text"
            name="condominiumname"
            placeholder="Nombre"
            onChange={(e) => setNameValidation(e.target.value)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button onClick={() => collapseOneOpen(!collapseOne)}>Cancelar</Button>
        <Button
          data-testid="delete-button"
          disabled={!validateCondominiumNameOnDelete()}
          color="danger"
          onClick={handleDelete}
        >
          Eliminar
        </Button>
      </ModalFooter>
    </Modal>
  );

  const handleBalanceModal = () => (
    <Modal isOpen={balanceModal} toggle={() => setBalanceModal(!balanceModal)}>
      <ModalHeader toggle={() => setBalanceModal(!balanceModal)}>Agregar saldo a {title}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Monto a agregar</Label>
          <NumberFormat
            value={balanceData.amount}
            thousandSeparator={true}
            decimalScale={2}
            fixedDecimalScale={true}
            allowNegative={false}
            className="form-control"
            placeholder="0.00"
            prefix={`${currencySymbol} `}
            onValueChange={({ value }) => {
              setBalanceData({ ...balanceData, amount: value });
            }}
          />
          {!isBaseCurrencyBank && (
            <div className="text-700 fs--1 mt-1">
              {isRateLoading ? (
                <span>Consultando tasa...</span>
              ) : Number(exchangeRate || 0) > 0 ? (
                <div>
                  {rateHelpText} | Equivalente aproximado: {" "}
                  <NumberFormat
                    displayType="text"
                    value={convertedBalanceAmount}
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    prefix={`${baseCurrency?.symbol || "$"} `}
                  /> {baseCurrency?.iso || "USD"}
                </div>
              ) : (
                <span>No hay una tasa de cambio disponible.</span>
              )}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          <Label>Descripción</Label>
          <Input
            type="text"
            value={balanceData.description}
            onChange={(e) => setBalanceData({ ...balanceData, description: e.target.value })}
            placeholder="Ej: Deposito en ventanilla"
          />
        </FormGroup>
        <FormGroup className="mb-0">
          <FileUploadArea
            label="Comprobante de pago (opcional)"
            accept="image/*,application/pdf"
            onChange={(e) => {
              const f = e.target.files[0];
              if (f) {
                setVoucherFile(f);
                setVoucherPreview(URL.createObjectURL(f));
              }
            }}
            preview={voucherPreview}
            fileName={voucherFile?.name}
            onRemove={() => {
              setVoucherFile(null);
              setVoucherPreview(null);
            }}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setBalanceModal(!balanceModal)}>Cancelar</Button>
        <Button color="primary" onClick={handleAddBalance}>Agregar saldo</Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <>
      <tr>
        <td style={{ whiteSpace: 'nowrap' }}>
          <b>{owner_dni}</b>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>{title}</td>
        <td style={{ whiteSpace: 'nowrap' }}>{owner_name}</td>
        <td style={{ whiteSpace: 'nowrap' }}>{currencyLabel}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {account_number ? (
            <NumberFormat
              thousandSeparator={true}
              displayType="text"
              format="#### #### #### #### #### ####"
              value={account_number}
            />
          ) : (
            "-"
          )}
        </td>
        <td>{email || "-"}</td>
        <td className="text-center">
          {createdAt && moment(createdAt).format("DD/MM/YYYY")}
        </td>
        {options && (
          <td className="text-right">
            <UncontrolledDropdown direction="left">
              <DropdownToggle color="falcon-default" size="sm">
                <FontAwesomeIcon icon="ellipsis-h" />
              </DropdownToggle>
              <DropdownMenu>
                {/* <DropdownItem onClick={() => handleDropdownAction("edit")}>
                  Editar
                </DropdownItem> */}
                <DropdownItem onClick={() => handleDropdownAction("add-balance")}>
                  Agregar saldo
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem
                  className="text-danger"
                  onClick={() => handleDropdownAction("delete")}
                >
                  Eliminar
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          </td>
        )}
      </tr>
      {handleModal()}
      {handleBalanceModal()}
    </>
  );
};

export default BankListItem;
